import { Avatar, Box, Divider, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { upperCase } from 'lodash';
import StepperVertical from '../../components/Stepper/StepperVertical';
import { RootState } from '../../redux/store';
import { getPayment, getPaymentHistory } from '../../services/vehicles';
import FieldList, { Field } from './FieldList';
import TabLoader from './TabLoader';

type HistoryProps = {
  history: any[];
  onClose: () => void;
};

const IconContent = () => (
  <Stack width={16} height={16} bgcolor="#000" borderRadius="50%" alignItems="center" justifyContent="center">
    <Icon icon="akar-icons:money" fontSize={10} color="#fff" />{' '}
  </Stack>
);

const History: FC<HistoryProps> = ({ history, onClose }) => {
  const steps = useMemo(
    () =>
      history.map((historyItem: any, i) => ({
        id: i.toString(),
        label: (
          <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
            <Typography fontSize={14} fontWeight={500} color="#000" sx={{ ml: '5px' }}>
              {moment(historyItem.paymentDate).format('MMM D YYYY')}
            </Typography>
            <Typography fontSize={14} fontWeight={500} color="#1C1951">
              $ {historyItem.amount}
            </Typography>
          </Stack>
        ),
        content: (
          <>
            <Box>
              <Typography
                fontSize={12}
                fontWeight={500}
                mt={1}
                lineHeight={1}
                color="#3E3E3E"
                textTransform="capitalize"
              >
                <Box fontWeight={400} component="span">
                  Payment Number:
                </Box>{' '}
                <Box fontWeight={500} color="#456BD9" component="span">
                  {historyItem.lastFour}
                </Box>
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                mt={1}
                lineHeight={1}
                color="#3E3E3E"
                textTransform="capitalize"
              >
                <Box fontWeight={400} component="span">
                  Refund status:
                </Box>{' '}
                {historyItem.status}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                mt={1}
                lineHeight={1}
                color="#3E3E3E"
                textTransform="capitalize"
              >
                <Box fontWeight={400} component="span">
                  Payment Reason:
                </Box>{' '}
                {historyItem.reason}
              </Typography>
              <Typography
                fontSize={12}
                fontWeight={500}
                mt={1}
                lineHeight={1}
                color="#3E3E3E"
                textTransform="capitalize"
              >
                <Box fontWeight={400} component="span">
                  Payment method:
                </Box>{' '}
                {historyItem.method}
              </Typography>
            </Box>
            {i !== history.length - 1 && (
              <Divider variant="inset" sx={{ ml: '0', mt: '25px', borderBottom: '1px dashed #E3E3E3' }} />
            )}
          </>
        ),
      })),
    [history]
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2.5} pr={1.5} pl={1.5}>
        <Typography fontSize={12} fontWeight={500} sx={{ textTransform: 'uppercase' }}>
          Payment History
        </Typography>
        <Typography
          fontSize={12}
          fontWeight={500}
          color="primary.main"
          sx={{ cursor: 'pointer' }}
          onClick={() => onClose()}
        >
          Back to Payment
        </Typography>
      </Stack>
      <Box mt={2.6} pl={1.5} pr={2}>
        <StepperVertical steps={steps} activeStep={steps.length} Icon={IconContent} />
      </Box>
    </Box>
  );
};

const Payment = () => {
  const { reservationId } = useParams();
  const [data, setData] = useState<any>({});
  const [openHistory, setOpenHistory] = useState(false);
  const lucidId = useSelector((state: RootState) => state.vehicle.data.lucidId);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    getPayment(reservationId || '').then((response) => {
      setData(response);
    });
  }, [reservationId]);

  useEffect(() => {
    if (openHistory) {
      getPaymentHistory(reservationId || '', lucidId).then((response) => {
        setHistory(response);
      });
    }
  }, [lucidId, openHistory, reservationId]);

  if (!data || Object.keys(data).length === 0) {
    return <TabLoader />;
  }

  const payment: Field[] = [
    {
      field: 'Payment Method',
      value: data?.paymentMethod,
    },
    {
      field: 'Payment Date',
      value: moment(data?.paymentDate).format('MMM DD, YYYY'),
    },
    {
      field: 'Remaining Amount',
      value: '',
    },
    {
      field: 'Total Price',
      value: data?.totalPrice ? `$${data?.totalPrice}` : '$0',
    },
    {
      field: 'Total payments',
      value: data?.vehicleDetails?.totalPayments ? `$${data?.vehicleDetails?.totalPayments}` : '$0',
    },
    {
      field: 'Total Refunds',
      value: data?.vehicleDetails?.totalRefunds ? `$${data.vehicleDetails?.totalRefunds}` : '$0',
    },
    {
      field: 'Net payments',
      value: data?.vehicleDetails?.netPayments ? `$${data.vehicleDetails?.netPayments}` : '$0',
    },
    {
      field: 'Delivery day payment method',
      value: data?.deliveryDayPaymentMethod,
    },
    {
      field: 'Delivery day amount',
      value: data?.remainingAmount,
    },
    {
      field: 'Lucid financing amount',
      value: data?.lucidFinancingAmount,
    },
    {
      field: 'other financing amount',
      value: data?.otherFinancingAmount,
    },
    {
      field: 'Lien holder name',
      value: data?.lienHolderAddress,
    },
    {
      field: 'Lien holder address',
      value: data?.lienHolderAddress,
    },
    {
      field: 'Payment status',
      value: '-',
    },
  ];

  return (
    <Box>
      {openHistory ? (
        <History history={history} onClose={() => setOpenHistory(false)} />
      ) : (
        <>
          <Typography
            variant="h6"
            pt={2.5}
            pr={1.5}
            pl={1.5}
            pb={2.5}
            fontSize={12}
            fontWeight={500}
            sx={{ textTransform: 'uppercase' }}
          >
            Purchase plan status
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" pr={1.5} pl={1.5}>
            <Typography fontSize={14} fontWeight={400} sx={{ color: '#3E3E3E' }}>
              Status
            </Typography>
            <Typography fontSize={14} fontWeight={500} color="#000">
              PA outstanding
            </Typography>
          </Stack>
          <Divider variant="inset" sx={{ ml: '10px', mr: '10px', mt: '20px', borderBottom: '1px dashed #E3E3E3' }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2.5} pr={1.5} pl={1.5}>
            <Typography fontSize={12} fontWeight={500} sx={{ textTransform: 'uppercase' }}>
              Payment
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={500}
              color="primary.main"
              sx={{ cursor: 'pointer' }}
              onClick={() => setOpenHistory(true)}
            >
              View Payment History
            </Typography>
          </Stack>
          <Stack
            direction="row"
            alignItems="normal"
            justifyContent="flex-start"
            sx={{
              background: '#E2EDFE',
              borderRadius: '4px',
              mt: '20px',
              mr: '10px',
              ml: '10px',
              mb: '10px',
              p: '10px',
            }}
          >
            <Avatar sx={{ width: '14px', minWidth: '14px', height: '14px', mr: '10px', background: 'none' }}>
              <Icon icon="ep:warning" fontSize="14px" color="#000" />
            </Avatar>
            <Stack alignItems="normal">
              <Typography sx={{ fontWeight: '500', fontSize: '12px', color: '#1F1F1F', lineHeight: '1.2' }}>
                <Box fontWeight={400} component="span">
                  Total capturable payments:
                </Box>{' '}
                $10,000.00
              </Typography>
              <Typography sx={{ fontWeight: '500', fontSize: '12px', color: '#1F1F1F', lineHeight: '1.2' }}>
                <Box fontWeight={400} component="span">
                  Payment will be reflected on
                </Box>{' '}
                Remaining Amount at Handoff{' '}
                <Box fontWeight={400} component="span">
                  once transaction is completed
                </Box>
              </Typography>
            </Stack>
          </Stack>
          <Divider variant="inset" sx={{ ml: '10px', mr: '10px', mt: '25px', borderBottom: '1px dashed #E3E3E3' }} />
          <FieldList fields={payment} />
        </>
      )}
    </Box>
  );
};

export default Payment;
